import { GradientDirection } from '#/service/constants';

/** List of colors which will always be defined. Please update this list when you add or remove color in the defaultThemeConfig */
const _DEFAULT_COLOR_KEYS = [
  'buttonPrimaryBackground',
  'buttonPrimaryBorder',
  'buttonPrimaryForeground',
  'buttonPromoBackground',
  'buttonPromoBorder',
  'buttonPromoForeground',
  'buttonSecondaryBackground',
  'buttonSecondaryBorder',
  'buttonSecondaryForeground',
  'pageBackground',
  'pageSecondaryBackground',
  'pageHeadingForeground',
  'pageParagraphForeground',
  'menuOpaqueBackground',
  'pageLinkForeground',
  'inputBackground',
  'inputForeground',
  'selectionControlsBackground',
  'videoPlayerOverlayBackground',
  'videoPlayerOverlayForeground',
] as const;

const _COLOR_KEYS = [
  'bannerExceptionBackground',
  'bannerExceptionForeground',
  'bannerInfoBackground',
  'bannerInfoForeground',
  'buttonPrimaryBackground',
  'buttonPrimaryBorder',
  'buttonPrimaryForeground',
  'buttonPromoBackground',
  'buttonPromoBorder',
  'buttonPromoForeground',
  'buttonSecondaryBackground',
  'buttonSecondaryBorder',
  'buttonSecondaryForeground',
  'dialogHeadingForeground',
  'dialogOverlayBackground',
  'dialogParagraphForeground',
  'iapForeground',
  'iapGradientBottom',
  'iapGradientTop',
  'imageOverlayBackground',
  'imageOverlayHeadingForeground',
  'imageOverlayHighlightForeground',
  'imageOverlayLinkForeground',
  'imageOverlayParagraphForeground',
  'inputBackground',
  'inputForeground',
  'inputValidation',
  'itemFocusFrame',
  'menuForeground',
  'menuGradientBackground',
  'menuHighlightBackground',
  'menuHighlightForeground',
  'menuOpaqueBackground',
  'pageBackground',
  'pageHeadingForeground',
  'pageHighlightForeground',
  'pageLinkForeground',
  'pageParagraphForeground',
  'pageSecondaryBackground',
  'selectionControlsBackground',
  'selectionControlsForeground',
  'snackbarBackground',
  'snackbarForeground',
  'snackbarActionForeground',
  'splashScreenBackground',
  'splashScreenForeground',
  'tabMenuBackground',
  'tabMenuForeground',
  'tabMenuHighlightBackground',
  'tveFeaturedBackground',
  'videoPlayerOverlayBackground',
  'videoPlayerOverlayForeground',
  'videoPlayerProgressBarForeground',
  'videoPlayerAdPod',
  'videoPlayerButtonPrimaryBackground',
  'videoPlayerButtonPrimaryForeground',
  'videoPlayerButtonSecondaryBackground',
  'videoPlayerButtonSecondaryForeground',
  'videoPlayerLiveIndicator',
] as const;

const _ALLOWED_GRADIENT_COLOR_KEYS = [
  'buttonPrimaryBackground',
  'buttonPrimaryBorder',
  'buttonPromoBackground',
  'buttonPromoBorder',
  'buttonSecondaryBackground',
  'buttonSecondaryBorder',
] as const;

export type ColorKey = (typeof _COLOR_KEYS)[number];
type DefaultColorKey = (typeof _DEFAULT_COLOR_KEYS)[number];
type GradientColorKey = (typeof _ALLOWED_GRADIENT_COLOR_KEYS)[number];

export type GradientColors = Array<{
  color: string;
  position?: number;
}>;

export type GradientColor = Partial<
  Record<
    GradientColorKey,
    {
      direction?: keyof typeof GradientDirection;
      colors: GradientColors;
    }
  >
>;

export const isGradientColorKey = (key: string): key is GradientColorKey =>
  _ALLOWED_GRADIENT_COLOR_KEYS.includes(key);

// A few types to achieve proper types for theme.color
// https://stackoverflow.com/questions/49682569/typescript-merge-object-types

type Id<T> = T extends infer U ? { [K in keyof U]: U[K] } : never;

// Names of properties in T with types that include undefined
type OptionalPropertyNames<T> = {
  [K in keyof T]: undefined extends T[K] ? K : never;
}[keyof T];

// Common properties from L and R with undefined in R[K] replaced by type in L[K]
type SpreadProperties<L, R, K extends keyof L & keyof R> = {
  [P in K]: L[P] | Exclude<R[P], undefined>;
};

type SpreadTwo<L, R> = Id<
  Pick<L, Exclude<keyof L, keyof R>> &
    Pick<R, Exclude<keyof R, OptionalPropertyNames<R>>> &
    Pick<R, Exclude<OptionalPropertyNames<R>, keyof L>> &
    SpreadProperties<L, R, OptionalPropertyNames<R> & keyof L>
>;

type Spread<A extends readonly [...any]> = A extends [infer L, ...infer R]
  ? SpreadTwo<L, Spread<R>>
  : unknown;

/** Colors library that is a combination of default colors that are always defined and optional (nullable) other colors */
export type ColorsLibrary = Spread<
  [
    Record<DefaultColorKey, string>,
    Exclude<Partial<Record<ColorKey, string>>, Record<DefaultColorKey, string>>,
  ]
>;
